import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import {
    ContentStackEvent,
    ContentStackItem,
    ContentStackService,
} from '@app/core/_services/content-stack.service'
import { RpHomeComponent } from './components/rp-home/rp-home.component'
import { RightPanelHeaderComponent } from './components/right-panel-header/right-panel-header.component'
import { LocalStorageAccessService } from '@app/core/_services/local-storage-access.service'
import { Subject, takeUntil } from 'rxjs'
import { ButtonModule } from 'primeng/button'
import { TooltipModule } from 'primeng/tooltip'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { faHandWave } from '@fortawesome/pro-regular-svg-icons'
import { AnalyticsService } from '@app/core/_services/analytics.service'
import { DialogService } from '@app/core/_services/dialog.service'

@Component({
    selector: 'fm-right-panel',
    standalone: true,
    imports: [
        CommonModule,
        RpHomeComponent,
        RightPanelHeaderComponent,
        ButtonModule,
        TooltipModule,
        FontAwesomeModule,
    ],
    templateUrl: './right-panel.component.html',
    styleUrl: './right-panel.component.scss',
})
export class RightPanelComponent implements OnInit {
    faHandWave = faHandWave
    panelOpen: boolean
    currentContent: ContentStackItem
    private ngUnsubscribe: Subject<any> = new Subject<any>()

    public contentAnimationClass = 'animation-fade-in'

    constructor(
        private localStorageAccessService: LocalStorageAccessService,
        public contentStackService: ContentStackService,
        private analyticsService: AnalyticsService,
        private cdr: ChangeDetectorRef,
        private dialogService: DialogService,
    ) {
        const panelExpandedStoredValue =
            this.localStorageAccessService.getItem('dynamicPanel:expanded')
        this.panelOpen = panelExpandedStoredValue === 'true' || panelExpandedStoredValue == null
    }

    ngOnInit(): void {
        this.contentStackService.contentStackChanged$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((event: ContentStackEvent) => {
                this.currentContent = event.currentContent
                if (this.currentContent && !this.panelOpen) {
                    this.contentAnimationClass = 'animation-fade-in'
                    this.onPanelToggled(true)
                } else {
                    // do this to "restart" the animation of the existing element
                    this.contentAnimationClass = ''
                    let newAnimationClass
                    if (event.transition === 'push') {
                        newAnimationClass = 'animation-slide-left-and-fade-in'
                    } else if (event.transition === 'pop') {
                        newAnimationClass = 'animation-slide-right-and-fade-in'
                    } else if (event.transition === 'same') {
                        newAnimationClass = 'animation-z-bounce'
                    } else {
                        newAnimationClass = 'animation-fade-in'
                    }

                    requestAnimationFrame(() => {
                        this.contentAnimationClass = ''
                        this.cdr.detectChanges()
                        requestAnimationFrame(() => {
                            this.contentAnimationClass = newAnimationClass
                            this.cdr.detectChanges()
                        })
                    })
                }
            })
    }

    onRefreshSelected(): void {
        this.contentStackService.refreshSelected()
    }

    onPanelToggled(openDueToContentPush: boolean = false): void {
        this.panelOpen = !this.panelOpen
        this.localStorageAccessService.setItem('dynamicPanel:expanded', this.panelOpen.toString())
        this.contentStackService.panelToggled()
        this.analyticsService.logDynamicPanelStateChange({
            state: this.panelOpen ? 'open' : 'closed',
            trigger: openDueToContentPush ? 'contentPush' : 'user',
        })
    }

    openHelpFeedbackModal(): void {
        this.analyticsService.logClickedEvent(
            'Help and Feedback',
            this.panelOpen ? 'dynamicPanelHomePanelOpen' : 'dynamicPanelHomePanelClosed',
        )
        this.dialogService.openHelpFeedbackModal()
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
